import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { App, Divider, Flex, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"

import Button from "../../Components/Button"
import EmptyComponent from "../../Components/Empty"
import ExportDropdown from "../../Components/Export"
import Filters from "../../Components/Filters"
import GlobalFilterTestData from "../../Components/GlobalFilterTestData/globalFilterTest"
import ManageColumn from "../../Components/ManageColumns"
import Table from "../../Components/Table"
import { getBaseColumns } from "../../Components/Table/baseColumns"
import Icon from "../../Icon"
import { serializeData } from "../../Utils/utils"
import GlobalCreators from "../GlobalFilters/reducer"
import InvoiceCreators from "../InvoiceHistoryBlock/reducer"
import { ChangeAmountModal } from "./components/ChangeAmountModal"
import { CommentModal } from "./components/CommentModal"
import {
  changeStatusConfirm,
  confirmInvoiceAmount,
  confirmUpdatedInvoiceAmount
} from "./components/helpers"
import { getActionColumn, getColumns, getDefaultColumn } from "./components/Table/columns"
import Creators, { selectAreFiltersDefault } from "./reducer"

import "./styles.less"

const pageSize = 50

const { Title, Text } = Typography

const AdminInvoicesPage = ({
  isMobile,
  isAdmin,
  businessAccountId,
  visibleFiltersAdmin,
  defaultColumnsAdmin,
  columnsAdmin,
  fetching,
  result,
  filters,
  filterOptions,
  exportFetching,
  exportResult,
  statistics,
  test_data,
  statusOptions,
  ...props
}) => {
  const { t } = useTranslation()
  const { notification, modal } = App.useApp()

  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState(null)
  const [modalType, setModalType] = useState(false)
  const lastRequestKeyRef = useRef(null)

  const isFiltersDefault = useSelector(selectAreFiltersDefault)

  const { count, results } = result

  const myColumns = columnsAdmin
  const allFilters = defaultColumnsAdmin
  const visibleFilters = visibleFiltersAdmin

  const myColumnsLength = myColumns.filter(({ active }) => active).length
  const myColumnsHash = myColumns
    .filter(({ active }) => active)
    .map(({ field_id }) => field_id)
    .join("")

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content", width: 475 }
  }

  const handleExport = useCallback(
    (type) => {
      const serializeFilters = serializeData(filters)
      const data = {
        ...(type !== "all" && serializeFilters),
        ...(type !== "all" ? { test_data } : {})
      }
      props.getAdminInvoicesExport(data)
    },
    [filters, test_data]
  )

  useEffect(() => {
    document.title = "Invoices - Chargeback"
    props.getAdminInvoicesFilterOptions()
    if (!statusOptions?.length) {
      props.getInvoicesStatusOptions()
    }
  }, [])

  const invoicesRequestData = useMemo(() => {
    const serializeFilters = serializeData(filters)
    const columns = myColumns.filter(({ active }) => active).map(({ field_id }) => field_id)
    return {
      page: pageNumber,
      page_size: pageSize,
      ordering,
      ...serializeFilters,
      ...(columns.length && {
        columns: ["id", "status", "brand", ...columns].join(",")
      }),
      test_data
    }
  }, [filters, myColumnsLength, ordering, pageNumber, test_data])

  useEffect(() => {
    const requestKey = JSON.stringify({
      businessAccountId,
      ...invoicesRequestData
    })
    if (lastRequestKeyRef.current === requestKey) return

    lastRequestKeyRef.current = requestKey
    props.getAdminInvoices({ ...invoicesRequestData })
  }, [businessAccountId, invoicesRequestData])

  useEffect(() => {
    if (exportResult) {
      notification.success({
        message: t("Exporting"),
        description: t(`${exportResult?.message}`), // "Export in progress, you will be notified once it's done.",
        ...notificationConfig
      })
      props.clearExportResult()
    }
  }, [exportResult])

  const tableSettings = useMemo(
    () => ({
      scroll: {
        //x: (230 * myColumns.filter(({active}) => active).length),
        x: "max-content",
        y: "calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px))",
        scrollToFirstRowOnChange: true
      },
      pagination: {
        total: count,
        current: pageNumber,
        pageSize: pageSize
      }
    }),
    [myColumnsLength, count, pageNumber]
  )

  const modalProps = {
    modal,
    isMobile
  }

  const handleChangeStatus = ({ id, newStatus }) => {
    changeStatusConfirm(
      { id, status: newStatus },
      (data) => props.updateInvoiceStatus(data),
      modalProps
    )
  }

  const onConfirmSuccess = (message, description) => {
    notification.success({
      message: "Confirm",
      description: "The invoice is confirmed",
      ...notificationConfig
    })
    setModalType(false)
  }

  const handleConfirmAmount = ({ id }) => {
    confirmInvoiceAmount(
      { id },
      (data) => props.confirmInvoiceAmount({ ...data, onSuccess: onConfirmSuccess }),
      modalProps
    )
  }

  const handleConfirmUpdatedAmount = (data) => {
    confirmUpdatedInvoiceAmount(
      data,
      (newData) => props.changeInvoiceAmount({ ...newData, onSuccess: onConfirmSuccess }),
      modalProps
    )
  }

  const getTableColumns = useMemo(() => {
    let allColumns = {
      ...getBaseColumns({ isAdmin: true, onSelectRow: handleSelectRow }),
      ...getColumns(statusOptions, handleChangeStatus)
    }
    return [
      allColumns["status"],
      ...myColumns
        .filter(({ active }) => active)
        .map((data) => allColumns?.[data.field_id] || getDefaultColumn({ ...data })),
      getActionColumn({ onSelectRow: handleSelectRow })
    ]
  }, [myColumnsHash])

  const handleChangeTable = useCallback((pagination, filters, sorter) => {
    let ordering = null
    if (sorter) {
      if (Array.isArray(sorter)) {
        ordering = sorter
          ?.map(({ field, order }) => (order === "ascend" ? field : `-${field}`))
          .join(",")
      } else {
        if (sorter.order) {
          ordering = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
        }
      }
    }
    setPageNumber(pagination.current)
    setOrdering(ordering)
  }, [])

  const handleChangeFilter = useCallback((newData, type) => {
    if (newData?.email) {
      if (Array.isArray(newData.email) && newData.email.length > 0) {
        newData.email.forEach((email) => {
          if (email) props.addGlobalFilter("user_email", email.trim())
        })
      } else if (typeof newData.email === "string" && newData.email.trim()) {
        props.addGlobalFilter("user_email", newData.email.trim())
      } else {
        props.removeGlobalFilter("user_email")
      }
    }
    if (type === "changeVisible") {
      props.changeVisibleFilters("visibleFiltersAdmin", newData)
    } else {
      props.changeFilters(newData)
      setPageNumber(1)
    }
  }, [])

  const onClear = useCallback(() => {
    props.clearAdminInvoicesFilters()
    props.clearGlobalFilters()
  }, [])

  const handleChangeColumns = useCallback((newColumns) => {
    props.changeColumns("columnsAdmin", newColumns)
  }, [])

  function handleSelectRow(data, type) {
    // let business_account_id = data?.business_account?.business_account_id || data?.business_account?.id
    //download_invoice
    if (type === "download_invoice") {
      props.getInvoiceDownload({
        file_name: "Invoice",
        invoice_id: data?.id,
        format: ".pdf"
      })
    }
    if (type === "confirm_invoice_amount") {
      handleConfirmAmount(data)
    }
    if (type === "view_comment") {
      setModalType({ type: "comment", invoice_comment: data?.comment })
    }
    if (type === "change_amount") {
      setModalType({
        type: "change_amount",
        invoice_id: data?.id,
        invoice_amount: data?.invoice_amount
      })
    }
  }

  return (
    <div className="admin-invoices-page">
      <Title level={3} style={{ textAlign: "left", marginBottom: 16 }}>
        {`Invoices: ${count}`}
      </Title>

      <Divider />

      <Flex gap={32} align="center" justify="space-between" style={{ margin: "-4px 0 20px" }}>
        <Filters
          visibleFilters={visibleFilters}
          filterOptions={filterOptions}
          allFilters={allFilters}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          showClearBtn={!isFiltersDefault}
          onClearFilters={onClear}
          addGlobalFilter={props.addGlobalFilter}
          removeGlobalFilter={props.removeGlobalFilter}
        />

        <Space style={{ flexShrink: 0 }}>
          <GlobalFilterTestData setPageNumber={setPageNumber} />

          <ExportDropdown loading={exportFetching} onExport={handleExport} isAdmin={isAdmin} />

          <ManageColumn myColumns={myColumns} onChangeColumns={handleChangeColumns} />
        </Space>
      </Flex>

      <Table
        columns={getTableColumns}
        loading={fetching}
        dataSource={results}
        scroll={tableSettings.scroll}
        onChange={handleChangeTable}
        pagination={tableSettings.pagination}
        showSizeChanger={false}
        //withOutPagination={true}
        locale={{
          emptyText: !statistics?.["shopify_invoice_counter"] ? (
            <EmptyComponent type="no_data_available" />
          ) : (
            <EmptyComponent type="search">
              <Button
                className="flex"
                icon={<Icon type={"menu_search"} />}
                onClick={props.clearAdminInvoicesFilters}
              >
                Clear search
              </Button>
            </EmptyComponent>
          )
        }}
      />

      {modalType?.type === "comment" && (
        <CommentModal setModalType={setModalType} invoiceComment={modalType?.invoice_comment} />
      )}

      {modalType?.type === "change_amount" && (
        <ChangeAmountModal
          setModalType={setModalType}
          modalType={modalType}
          fetching={fetching}
          handleConfirmUpdatedAmount={handleConfirmUpdatedAmount}
        />
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  businessAccountId: state.auth.userInfo.current_business_account_id,
  isAdmin: state.auth.isAdmin,
  isMobile: state.nav.isMobile,

  visibleFiltersAdmin: state.adminInvoices.visibleFiltersAdmin,

  defaultColumns: state.adminInvoices.defaultColumns,
  defaultColumnsAdmin: state.adminInvoices.defaultColumnsAdmin,

  columns: state.adminInvoices.columns,
  columnsAdmin: state.adminInvoices.columnsAdmin,

  fetching: state.adminInvoices.fetching,
  result: state.adminInvoices.result,

  filters: state.adminInvoices.filters,
  filterOptions: state.adminInvoices.filterOptions,

  exportFetching: state.adminInvoices.exportFetching,
  exportResult: state.adminInvoices.exportResult,

  statistics: state.businessAccounts.statistics,
  test_data: state.globalFilters.testData,
  statusOptions: state.invoices.statusOptions //reused from invoice history
})

const mapDispatchToProps = (dispatch) => ({
  getAdminInvoicesFilterOptions: () => dispatch(Creators.getAdminInvoicesFilterOptionsRequest()),

  getAdminInvoices: (params) => dispatch(Creators.getAdminInvoicesRequest(params)),

  getAdminInvoicesExport: (params) => dispatch(Creators.getAdminInvoicesExportRequest(params)),
  clearExportResult: () => dispatch(Creators.adminInvoicesSetProp("exportResult", null)),
  changeColumns: (key, columns) => dispatch(Creators.adminInvoicesSetProp(key, columns)),
  changeFilters: (filters) => dispatch(Creators.adminInvoicesSetProp("filters", filters)),
  changeVisibleFilters: (visibleFiltersType, filters) =>
    dispatch(Creators.adminInvoicesSetProp(visibleFiltersType, filters)),
  clearAdminInvoicesFilters: () => dispatch(Creators.clearAdminInvoicesFilters()),

  clearGlobalFilters: () => dispatch(GlobalCreators.clearGlobalFilters()),
  addGlobalFilter: (key, value) => dispatch(GlobalCreators.addGlobalFilter(key, value)),
  removeGlobalFilter: (key) => dispatch(GlobalCreators.removeGlobalFilter(key)),

  getInvoiceDownload: (data) => dispatch(InvoiceCreators.getInvoiceDownloadRequest(data)),
  getInvoicesStatusOptions: (params) =>
    dispatch(InvoiceCreators.getInvoicesStatusOptionsRequest(params)),
  updateInvoiceStatus: (data) => dispatch(Creators.updateAdminInvoiceRequest(data)),
  confirmInvoiceAmount: (data) => dispatch(Creators.confirmInvoiceRequest(data)),
  changeInvoiceAmount: (data) => dispatch(Creators.changeInvoiceAmountRequest(data))
})

export default connect(mapStateToProps, mapDispatchToProps)(AdminInvoicesPage)
